import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AgencyProvider } from "@/context/AgencyContext";
import { CartProvider } from "@/context/CartContext";
import { Toaster } from "@/components/ui/sonner";

import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AppShell from "@/components/AppShell";
import DashboardPage from "@/pages/DashboardPage";
import ManufacturersPage from "@/pages/ManufacturersPage";
import ManufacturerDetailPage from "@/pages/ManufacturerDetailPage";
import UsersPage from "@/pages/UsersPage";
import ProfilePage from "@/pages/ProfilePage";
import SearchPage from "@/pages/SearchPage";
import AgenciesPage from "@/pages/AgenciesPage";
import AgencyDetailPage from "@/pages/AgencyDetailPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import PublicRegisterPage from "@/pages/PublicRegisterPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import SupportPage from "@/pages/SupportPage";
import QuoteHistoryPage from "@/pages/QuoteHistoryPage";
import NotificationsAdminPage from "@/pages/NotificationsAdminPage";
import NotificationsInboxPage from "@/pages/NotificationsInboxPage";
import MarketingPage from "@/pages/MarketingPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import PushRegistrar from "@/components/PushRegistrar";

function AuthGate({ children }) {
  /** Pure auth check — no role gating. Used as the outer wrapper for the shell. */
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground font-mono-data">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function ProtectedRoute({ children, adminOnly = false, allowAgencyUser = false, allowDistributor = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground font-mono-data">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Agency users only get access to /approvals and /profile
  if (user.role === "agency_user" && !allowAgencyUser) {
    return <Navigate to="/approvals" replace />;
  }
  if (user.role === "distributor" && !allowDistributor) {
    return <Navigate to="/search" replace />;
  }
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Role-based landing FIRST; the must-change-password banner is shown on
  // /profile when they visit it, but their initial landing should match
  // their role.
  if (user.role === "agency_user") return <Navigate to="/approvals" replace />;
  if (user.role === "distributor") return <Navigate to="/search" replace />;
  if (user.must_change_password) return <Navigate to="/profile" replace />;
  return <DashboardPage />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AgencyProvider>
          <CartProvider>
            <PushRegistrar />
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/register/:agencyId" element={<PublicRegisterPage />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route
                path="/"
                element={
                  <AuthGate>
                    <AppShell />
                  </AuthGate>
                }
              >
                <Route index element={<RootRedirect />} />
                <Route
                  path="manufacturers"
                  element={
                    <ProtectedRoute>
                      <ManufacturersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="manufacturers/:id"
                  element={
                    <ProtectedRoute>
                      <ManufacturerDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="search"
                  element={
                    <ProtectedRoute allowAgencyUser allowDistributor>
                      <SearchPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="agencies"
                  element={
                    <ProtectedRoute adminOnly>
                      <AgenciesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="agencies/:id"
                  element={
                    <ProtectedRoute adminOnly>
                      <AgencyDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="users"
                  element={
                    <ProtectedRoute adminOnly>
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="approvals"
                  element={
                    <ProtectedRoute allowAgencyUser>
                      <ApprovalsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="profile"
                  element={
                    <ProtectedRoute allowAgencyUser allowDistributor>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="quotes"
                  element={
                    <ProtectedRoute allowDistributor>
                      <QuoteHistoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="notifications"
                  element={
                    <ProtectedRoute adminOnly>
                      <NotificationsAdminPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="inbox"
                  element={
                    <ProtectedRoute allowAgencyUser allowDistributor>
                      <NotificationsInboxPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="marketing"
                  element={
                    <ProtectedRoute allowAgencyUser>
                      <MarketingPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster richColors position="top-right" />
          </CartProvider>
        </AgencyProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
